<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { appContainer } from "../../container";
import { InvitedUserInterface, Role } from "../../interface/UserInterface";
import { PagingInterface } from "../../interface/PaginationInterface";
import { LoadingService } from "../../services/loadingService";
import { UserService } from "../../services/userService";
import UserCardRaterParticipant from "./components/UserCardRaterParticipant.vue";
import PaginationComponent from "../../components/PaginationComponent.vue";
import { ActiveRolesStore } from "../../stores/ActiveRolesStore";
import { useAdminCompaniesStore } from "../../stores/AdminCompaniesStore";

const isLoading = ref(true);
const loadingService = appContainer.resolve(LoadingService);
const userService = appContainer.resolve(UserService);
const activeRoleStore = ActiveRolesStore();
const adminCompaniesStore = useAdminCompaniesStore();

// Admins are scoped to the users of the companies they manage; super_admin
// keeps the existing system-wide, server-paginated listing.
const isAdminScoped = ref(false);

const pageConfig = ref<{ page?: number; offset?: number }>({
  page: 1,
  offset: 8,
});

// super_admin (server-side) data
const paginatedData = ref<PagingInterface<InvitedUserInterface>>();
const users = ref<InvitedUserInterface[]>([]);

// admin (client-side) data: the full merged list across the admin's companies
const adminAllUsers = ref<InvitedUserInterface[]>([]);

const searchField = ref("");
const invitationTypeArray = ref<string[]>([Role.Participants, Role.Raters]);

// --- admin client-side filtering + pagination ---
const filteredAdminUsers = computed(() => {
  let list = adminAllUsers.value.filter((user) =>
    invitationTypeArray.value.includes(user.invitation_type),
  );
  const query = searchField.value.trim().toLowerCase();
  if (query) {
    list = list.filter((user) => {
      const name =
        `${user.invited_user?.first_name ?? ""} ${user.invited_user?.last_name ?? ""}`.toLowerCase();
      return name.includes(query);
    });
  }
  return list;
});

const pagedAdminUsers = computed(() => {
  const start = (pageConfig.value.page! - 1) * pageConfig.value.offset!;
  return filteredAdminUsers.value.slice(
    start,
    start + pageConfig.value.offset!,
  );
});

// --- unified view bindings for the template (admin vs super_admin) ---
const displayedUsers = computed(() =>
  isAdminScoped.value ? pagedAdminUsers.value : users.value,
);
const totalItems = computed(() =>
  isAdminScoped.value
    ? filteredAdminUsers.value.length
    : (paginatedData.value?.totalItems ?? 0),
);
const currentPage = computed(() =>
  isAdminScoped.value ? pageConfig.value.page : paginatedData.value?.page,
);
const perPage = computed(() =>
  isAdminScoped.value ? pageConfig.value.offset : paginatedData.value?.offset,
);

onMounted(async () => {
  await activeRoleStore.getActiveRole();
  isAdminScoped.value =
    activeRoleStore.activeRoles.includes("admin") &&
    !activeRoleStore.activeRoles.includes("super_admin");
  await getUsers().then(() => (isLoading.value = false));
});

async function getUsers() {
  return isAdminScoped.value ? getAdminUsers() : getSuperAdminUsers();
}

// super_admin: system-wide listing, paginated by the server
async function getSuperAdminUsers() {
  const loader = await loadingService.show();
  await userService
    .getInvitedUser(
      {
        invitationTypeArray: invitationTypeArray.value,
        name: searchField.value,
        allUsers: true,
      },
      pageConfig.value,
    )
    .then((result) => {
      paginatedData.value = result;
      users.value = result.items;
    })
    .finally(() => loader.hide());
}

// admin: fetch all participants/raters of every company the admin manages,
// then merge them into one list (paginated/filtered client-side).
async function getAdminUsers() {
  const loader = await loadingService.show();
  try {
    // Reuse the company ids cached by the HasInvitation guard; fall back to a
    // one-time fetch if the store was never populated (e.g. a hard refresh).
    await adminCompaniesStore.loadCompanies().catch(() => undefined);

    const results = await Promise.all(
      adminCompaniesStore.companyIds.map((companyId) =>
        userService
          .getInvitedUser({
            companyId,
            invitationTypeArray: [Role.Participants, Role.Raters],
            invitation_sent: true,
            all: true,
          })
          .then((result) => result.items)
          .catch(() => [] as InvitedUserInterface[]),
      ),
    );

    // merge + dedupe by invitation id
    const byId = new Map<number, InvitedUserInterface>();
    results.flat().forEach((user) => byId.set(user.id, user));
    adminAllUsers.value = [...byId.values()];
  } finally {
    loader.hide();
  }
}

function searchUsers() {
  pageConfig.value.page = 1;
  if (isAdminScoped.value) {
    return;
  }
  if (searchField.value === "") {
    return;
  }
  getSuperAdminUsers();
}

function updatePage(pageNumber: number) {
  if (isAdminScoped.value) {
    pageConfig.value.page = pageNumber;
    return;
  }
  if (pageNumber === paginatedData.value?.page) {
    return;
  }
  pageConfig.value.page = pageNumber;
  getSuperAdminUsers();
}

watch(searchField, () => {
  if (isAdminScoped.value) {
    pageConfig.value.page = 1;
    return;
  }
  if (searchField.value === "") {
    pageConfig.value.page = 1;
    getSuperAdminUsers();
  }
});

watch(invitationTypeArray, () => {
  pageConfig.value.page = 1;
  if (!isAdminScoped.value) getSuperAdminUsers();
});
</script>

<template>
  <template v-if="!isLoading">
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h2 class="fw-medium fs-4 p-0">Users</h2>
    </div>
    <form
      class="d-flex w-100 justify-content-between flex-wrap mb-4"
      @submit.prevent="searchUsers"
    >
      <div class="search-wrapper d-flex m-0 position-relative w-100 gap-4">
        <input
          type="search"
          class="form-control"
          placeholder="Search"
          v-model="searchField"
        />
        <button type="submit" class="text-white btn bg-primary flex-center">
          Search
        </button>
      </div>
    </form>
    <ul
      class="nav nav-pills mb-3 justify-content-between justify-content-sm-start"
    >
      <li class="nav-item">
        <button
          class="nav-link"
          @click="invitationTypeArray = [Role.Participants, Role.Raters]"
          :class="{
            active:
              invitationTypeArray.includes(Role.Participants) &&
              invitationTypeArray.includes(Role.Raters),
          }"
        >
          All
        </button>
      </li>
      <li class="nav-item">
        <button
          class="nav-link"
          @click="invitationTypeArray = [Role.Participants]"
          :class="{
            active:
              invitationTypeArray.length === 1 &&
              invitationTypeArray.includes(Role.Participants),
          }"
        >
          Participants
        </button>
      </li>
      <li class="nav-item">
        <button
          class="nav-link"
          @click="invitationTypeArray = [Role.Raters]"
          :class="{
            active:
              invitationTypeArray.length === 1 &&
              invitationTypeArray.includes(Role.Raters),
          }"
        >
          Raters
        </button>
      </li>
    </ul>
    <div v-if="displayedUsers.length">
      <UserCardRaterParticipant
        v-for="user in displayedUsers"
        :key="user.id"
        :user="user"
      />
    </div>
    <div
      class="no-result d-flex align-items-center justify-content-center text-opacity-50 text-danger"
      v-else
    >
      No Records Found
    </div>
    <div class="pagination-wrapper" v-if="totalItems">
      <PaginationComponent
        v-if="totalItems > 8"
        :totalRows="totalItems"
        :currentPage="currentPage"
        :perPage="perPage"
        @pageChange="updatePage"
      />
    </div>
  </template>
</template>

<style scoped lang="scss">
.search-wrapper {
  max-width: 350px;
}

.nav-link {
  border-radius: 0;
  border-bottom: 3px solid transparent;
  &.active {
    background-color: transparent;
    color: var(--bs-primary);
    border-color: var(--bs-primary);
  }
}

@media (max-width: 575px) {
  .nav-link {
    font-size: 14px;
  }
}
</style>
